import { type CTNDropdownOptionProps } from '@ctn/ui/types/ctn-dropdown';
import { useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { type DireccionForm } from '@/components/Interviniente/config';
import { useGlobalContext } from '@/context/global.context';
import { getMunicipiosByCp } from '@/services/maestros.service';

import { type IMunicipiosBDCorpo } from '@/interfaces/model/maestros.interface';

import useApiData from './useApiData';

const useDireccion = <T extends Record<string, unknown> & { direccion: DireccionForm }>(context: UseFormReturn<T>) => {
	const {
		maestrosGeneric: { provincias },
	} = useGlobalContext();
	const { setValue, trigger, watch } = context as unknown as UseFormReturn<{ direccion: DireccionForm }>;

	/** 
	 * El listado de municipios únicamente ser cargan mediante el codigo postal.
	 * Para mostrar el listado de municipios con datos cargados, lo añadimos con los datos existentes
	 */ 
	const descripcionMunicipio = watch('direccion.descripcionMunicipio');
	const codigoMunicipio = watch('direccion.codigoMunicipio');
	const preloadMunicipios: CTNDropdownOptionProps[] | undefined = codigoMunicipio && descripcionMunicipio
		? [{ label: descripcionMunicipio, value: codigoMunicipio }]
		: undefined;

	const [municipios, setMunicipios] = useState<CTNDropdownOptionProps[]>([]);
	
	const { triggerFetch } = useApiData({ loading: ['getCodigoPostal'] });
	const [disabledMunicipio, setDisabledMunicipio] = useState(true);

	const mapMunicipioBDCorpo = (municipio: IMunicipiosBDCorpo[]): CTNDropdownOptionProps[] =>
		municipio.map(({ nombre, codigo }) => ({ label: nombre, value: codigo }));

	const getDescProvincia = (id: string) => provincias.find((provincia) => provincia.codigo === id)?.nombre;

	const setDefaultValues = () => {
		setValue('direccion.codProvincia', '');
		setValue('direccion.descripcionProvincia', '');
		setValue('direccion.codigoMunicipio', '');
		setValue('direccion.descripcionMunicipio', '');
	};

	const setValuesNotFound = () => {
		setValue('direccion.codProvincia', undefined);
		setValue('direccion.descripcionProvincia', undefined);
		setValue('direccion.codigoMunicipio', undefined);
		setValue('direccion.descripcionMunicipio', undefined);
	};

	/**
	 * Función que gestiona la lógica al cambiar el código postal.
	 * Valida el campo, realiza la petición para obtener los municipios asociados,
	 * y autocompleta la provincia y el municipio si es único.
	 */
	const handleCodigoPostal = async (value: string) => {
		setDisabledMunicipio(true);
		setDefaultValues();

		const isValid = await trigger('direccion.codigoPostal');
		if (!isValid) return;

		const { data, code } = await triggerFetch({
			fetch: getMunicipiosByCp,
			payload: value,
			name: 'getCodigoPostal',
		});

		if (!data && code === 200) {
			// Esto permite validar de nuevo el código postal como erroneo.
			setValuesNotFound();
			trigger('direccion.codigoPostal');
			return;
		}

		if (!data) return;

		const { municipio } = data;
		const { codigoProvincia } = municipio[0];
		setValue('direccion.codProvincia', codigoProvincia);
		setValue('direccion.descripcionProvincia', getDescProvincia(codigoProvincia));
		setMunicipios(mapMunicipioBDCorpo(municipio));
		if (municipio.length === 1) {
			const { codigo, nombre } = municipio[0];
			setValue('direccion.codigoMunicipio', codigo);
			setValue('direccion.descripcionMunicipio', nombre);
		} else {
			setDisabledMunicipio(false);
		}
		await trigger(['direccion.codigoMunicipio', 'direccion.codProvincia']);
	};

	return { handleCodigoPostal, municipios: preloadMunicipios ?? municipios, disabledMunicipio };
};

export default useDireccion;
